<template>
	<div class="d-flex min-vh-100 align-items-center two-factor-authentication">
		<CContainer>
			<CRow>
				<CCol>
					<div class="two-factor-authentication-form">
						<div class="logo">
							<FontAwesomeIcon
								class="icon"
								:icon="['fas', 'user-lock']"
							/>
						</div>

						<CForm @submit.prevent="handleSubmit">
							<h1 class="form-heading">
								{{ $t('page.twofactor.form.title') }}
							</h1>
							<!-- eslint-disable-next-line vue/no-v-html -->
							<p class="form-description" v-html="$t('page.twofactor.form.description')">
							</p>
							<BaseOtpInput
								ref="codeInput"
								v-model="code"
								class="form-input"
								:length="6"
								:has-error="errorCode"
								:disabled="isLoading"
								:aria-label="$t('page.twofactor.form.input.code.placeholder')"
								data-test-id="input-code"
								@input="handleCodeInput"
								@complete="handleComplete"
								@submit="handleSubmit"
							/>

							<p
								v-if="errorCode && errorCodeText"
								class="form-error"
								data-test-id="text-code-error"
							>
								{{ errorCodeText }}
							</p>

							<div class="resend-group">
								<CButton
									:disabled="isResendLoading || resendCountdown > 0"
									class="resend-button"
									data-test-id="button-resend"
									@click.prevent="handleResend"
								>
									{{ resendCountdown > 0 ? $t('page.twofactor.form.button.countdown', { countdown: resendCountdown }) : $t('page.twofactor.form.button.resend') }}
								</CButton>
							</div>
							<div class="text-center">
								<div class="button-group">
									<CButton
										color="secondary"
										class="back-button px-4"
										data-test-id="button-back"
										@click="onBack"
									>
										{{ $t('page.twofactor.form.button.back') }}
									</CButton>
									<CButton
										color="primary"
										class="submit-button px-4"
										data-test-id="button-submit"
										:disabled="isLoading"
										@click="handleSubmit"
									>
										<template v-if="isLoading">
											<FontAwesomeIcon
												class="check-spinner-third"
												:icon="['fas', 'spinner-third']"
											/>
										</template>
										<template v-else>
											{{ $t('page.twofactor.form.button.submit') }}
										</template>
									</CButton>
								</div>
							</div>
						</CForm>
					</div>
				</CCol>
			</CRow>
		</CContainer>

		<BaseAppVersion />
	</div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import { required, numeric, minLength, maxLength } from 'vuelidate/lib/validators';
import { pathOr } from '../assets/js/helpers';
import BaseAppVersion from '../components/BaseAppVersion.vue';
import BaseOtpInput from '../components/BaseOtpInput.vue';
import errorMessage from '../enums/errorMessage';
import { ROUTE_NAME } from '../enums/route';

export default {
	name: 'TwoFactorAuthentication',
	components: {
		BaseAppVersion,
		BaseOtpInput,
	},
	validations: {
		code: {
			required,
			numeric,
			minLength: minLength(6),
			maxLength: maxLength(6),
		},
	},
	data() {
		return {
			code: '',

			resendCountdown: 0, // countdown in seconds
			resendTimer: null,	// reference to the countdown interval

			// Last code sent to the API. Used so auto-submit fires once per
			// distinct code instead of re-submitting the same wrong one.
			lastSubmittedCode: null,
		};
	},
	computed: {
		...mapState({
			isLoading: (state) => state.auth.isLoading,
			errorVerify: (state) => state.auth.error,
			errorVerifyMessage: (state) => state.auth.errorMessage,

			isResendLoading: (state) => state.auth.resend.isLoading,
			errorResend: (state) => state.auth.resend.error,
			errorResendMessage: (state) => state.auth.resend.errorMessage,
			requestExpiredTime: (state) => state.auth.resend.requestExpiredTime,

			errorTwoFactorMessage: (state) => state.auth.twoFactor.errorMessage,
			redirect: (state) => state.auth.twoFactor.redirect,
		}),

		...mapGetters({
			getDefaultRouteName: 'profile/getDefaultRouteName',
		}),

		errorCode() {
			return this.errorVerify || pathOr(false, ['$v', 'code', '$error'])(this);
		},
		errorCodeText() {
			const codeValidation = this.$v.code;

			if (!codeValidation.required) {
				return this.$t(errorMessage.REQUIRED_FIELD);
			} else if (!codeValidation.numeric) {
				return this.$t(errorMessage.INVALID_NUMBER);
			} else if (!codeValidation.minLength || !codeValidation.maxLength) {
				return this.$t(errorMessage.INVALID_LENGTH);
			} else if (this.errorVerify) {
				return this.$t(this.errorVerifyMessage);
			}
			return null;
		},
	},
	mounted() {
		this.focusCodeInput();
	},
	beforeDestroy() {
		// Clear timer when component is destroyed
		if (this.resendTimer) {
			clearInterval(this.resendTimer);
			this.resendTimer = null;
		}
	},
	methods: {
		...mapActions({
			postSocialVerify: 'auth/postSocialVerify',
			postSocialResend: 'auth/postSocialResend',
			clearVerifySocialError: 'auth/clearVerifySocialError',
			clearTwoFactorError: 'auth/clearTwoFactorError',

			getProfile: 'profile/getProfile',
		}),

		onBack() {
			this.$router.push({ name: ROUTE_NAME.LOGOUT });
		},
		async handleResend() {
			// Do nothing if already loading
			if (this.isResendLoading) {
				return;
			}

			await this.postSocialResend();

			// A new code was requested: allow auto-submit to fire again
			this.lastSubmittedCode = null;
			this.focusCodeInput({ select: true });

			// Reset countdown to 60 seconds
			let remainingSeconds = 60;

			if (this.requestExpiredTime) {
				const expireAt = new Date(this.requestExpiredTime);
				expireAt.setHours(expireAt.getHours() + 7);
				const now = new Date();

				const diff = Math.floor((expireAt - now) / 1000);
				if (!isNaN(diff) && diff > 0) {
					remainingSeconds = diff;
				}
			}

			this.resendCountdown = remainingSeconds;

			// Clear any existing timer
			if (this.resendTimer) {
				clearInterval(this.resendTimer);
			}

			// Start countdown timer
			this.resendTimer = setInterval(() => {
				if (this.resendCountdown > 0) {
					this.resendCountdown--;
				} else {
					clearInterval(this.resendTimer);
					this.resendTimer = null;
				}
			}, 1000);
		},
		focusCodeInput({ select = false } = {}) {
			this.$nextTick(() => {
				const otpInput = this.$refs.codeInput;

				if (!otpInput) {
					return;
				}

				if (select) {
					// Never clear the code on failure. Land on the first box with it
					// selected, so typing six digits replaces the whole thing cleanly.
					otpInput.focusIndex(0);
				} else {
					otpInput.focusFirstEmpty();
				}
			});
		},
		handleCodeInput() {
			this.clearVerifySocialError();

			// Drop the "submitted" flag again so the message clears while the
			// user is fixing the code, and comes back only on the next submit.
			this.$v.$reset();
		},
		handleComplete(code) {
			// Fired by BaseOtpInput only when the code has actually just been
			// finished - not on every keystroke of a correction.
			// `lastSubmittedCode` stops the same failed code being retried by itself.
			if (code === this.lastSubmittedCode) {
				return;
			}

			this.$nextTick(() => {
				this.handleSubmit();
			});
		},
		async handleSubmit() {
			if (this.isLoading) {
				return;
			}

			this.$v.$touch();

			if (this.$v.$invalid) {
				// Incomplete code: put the cursor back without wiping what they typed
				this.focusCodeInput();
				return;
			}

			this.lastSubmittedCode = this.code;

			await this.postSocialVerify({
				code: this.code,
			});

			if (this.errorVerify) {
				// Never clear the code on failure - keep it and select it so the
				// user can retype over it or fix a single digit.
				this.focusCodeInput({ select: true });
				return;
			}

			await this.getProfile();
			// Clear errors
			this.clearTwoFactorError();

			const routeName = this.getDefaultRouteName;
			if (this.redirect && this.redirect !== '/login') {
				this.$router.push(this.redirect);
			} else {
				// Navigate using Vue Router (SPA style)
				this.$router.push({ name: routeName });
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.two-factor-authentication {
	position: relative;

	.logo {
		width: 88px;
		height: 88px;
		margin: 0 auto 24px;
		background-color: #eaeaea;
		border-radius: 50%;
		display: flex;
		justify-content: center;
		align-items: center;

		.icon {
			font-size: rem(40);
			color: #fff;
			line-height: 1;
			margin: 0;
			text-align: center;
		}
	}

	&-form {
		background-color: #fff;
		padding: 24px;
		border-radius: 8px;
		box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
		max-width: 360px;
		margin: 0 auto;

		.form-heading {
			font-size: 20px;
			text-align: center;
		}

		.form-description {
			font-size: 14px;
			text-align: center;
		}

		.form-input {
			margin-bottom: rem(12);
		}

		.form-error {
			margin-bottom: rem(8);
			color: $color-alert;
			font-size: rem(12);
			text-align: center;
		}

		.resend-group {
			display: flex;
			justify-content: center;
			margin-bottom: rem(20);
		}
	}
}

.button-group {
	display: flex;
	gap: 8px;
}

.back-button,
.submit-button {
	flex: 1;
}

.submit-button {
	.check-spinner-third {
		margin-right: 4px;
		animation: spin 1s linear infinite;
	}
}

.resend-button {
	font-size: rem(12);
	font-weight: 600;
	color: $color-hypertext;
	padding: 0;

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
}

@keyframes spin {
	from {
		transform: rotate(0deg);
	}

	to {
		transform: rotate(360deg);
	}
}
</style>
